import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Phone, Clock, MapPin, Mail, Send, Loader, X, Star, CheckCircle } from 'lucide-react';
import { FaWhatsapp, FaWaze, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { createPortal } from 'react-dom';

const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(10)].map((_, i) => (
        <div
          key={`orb-${i}`}
          className="absolute rounded-full opacity-20 animate-pulse"
          style={{
            width: `${40 + Math.random() * 60}px`,
            height: `${40 + Math.random() * 60}px`,
            background: `radial-gradient(circle, ${
              i % 3 === 0 ? '#3B82F6' : i % 3 === 1 ? '#8B5CF6' : '#EC4899'
            }, transparent)`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${4 + Math.random() * 3}s`,
            transform: `scale(${0.5 + Math.random() * 0.5})`
          }}
        />
      ))}
      
      {[...Array(25)].map((_, i) => (
        <div
          key={`particle-${i}`}
          className="absolute bg-white/30 rounded-full animate-bounce"
          style={{
            width: `${2 + Math.random() * 4}px`,
            height: `${2 + Math.random() * 4}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${2 + Math.random() * 2}s`
          }}
        />
      ))}
    </div>
  );
};

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div 
        className="fixed inset-0 bg-black/80 transition-opacity backdrop-blur-sm" 
        onClick={onClose}
      />
      <div className="relative z-50 bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-md sm:w-full mx-auto border-t-2 sm:border border-blue-200 animate-slide-up">
        {children}
      </div>
    </div>,
    document.body
  );
};

const SuccessAnimation = () => (
  <div className="flex flex-col items-center justify-center p-8">
    <div className="relative">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
        <CheckCircle className="w-10 h-10 text-green-500" />
      </div>
      <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-ping"></div>
    </div>
    <h3 className="text-2xl font-bold text-gray-800 mt-4 mb-2">הפנייה נשלחה!</h3>
    <p className="text-gray-600 text-center">נחזור אליכם בהקדם האפשרי</p>
  </div>
);

const ContactPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [showHours, setShowHours] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    subject: '',
    debtAmount: '' // ✅ הוספנו שדה חדש לגובה החוב
  });

  // אפשרויות גובה חוב
  const debtAmountOptions = [
    { value: '0-50000', label: '0 - 50,000 ₪' },
    { value: '50000-170000', label: '50,000 - 170,000 ₪' },
    { value: '170000+', label: 'מעל 170,000 ₪' }
  ];

 // בדיקה אם הנושא הנבחר הוא חדלות פירעון
 const isExecutionSubject = () => {
  const selectedSubject = subjects.find(s => s.id === formData.subject);
  return selectedSubject && 
         (selectedSubject.title === 'חדלות פרעון והוצאה לפועל' || 
          selectedSubject.title.includes('חדלות פירעון'));
};

const handleInputChange = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({
    ...prev,
    [name]: value,
    // אם משנים נושא ולא בוחרים חדלות פירעון, מאפסים את גובה החוב
    ...(name === 'subject' && !subjects.find(s => s.id === value)?.title.includes('חדלות פירעון') 
      ? { debtAmount: '' } 
      : {})
  }));
};
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slide-up {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes gradient-shift {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
      
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
        50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.6); }
      }
      
      .animate-slide-up {
        animation: slide-up 0.3s ease-out;
      }
      
      .animate-gradient {
        background-size: 200% 200%;
        animation: gradient-shift 3s ease infinite;
      }
      
      .animate-float {
        animation: float 3s ease-in-out infinite;
      }
      
      .animate-glow {
        animation: glow 2s ease-in-out infinite;
      }
      
      .glass-effect {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.2);
      }
      
      .gradient-text {
        background: linear-gradient(135deg, #3B82F6, #8B5CF6, #EC4899);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const loadData = async () => {
    try {
      const businessDoc = await getDoc(doc(db, 'businessDetails', 'kioGnL9sTArCVO1W54OL'));
      setBusiness(businessDoc.data());

      const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
      const subjectsData = subjectsSnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().name
      }));
      

      
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // שליחה לפיירבייס עם הנתונים החדשים
      await addDoc(collection(db, 'leads'), {
        ...formData,
        createdAt: new Date().toISOString()
      });
      
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/thank-you');
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('אירעה שגיאה בשליחת הטופס. אנא נסו שוב.');
    } finally {
      setLoading(false);
    }
  };

  if (!business) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-900 via-slate-7000 to-slate-800">
        <div className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-blue-400" />
          <p className="text-white/80">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-7000 to-slate-800 overflow-x-hidden">
      <div className="absolute inset-0 opacity-40">
        <svg width="60" height="60" viewBox="0 0 60 60" className="w-full h-full">
          <defs>
            <pattern id="dots" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <circle cx="30" cy="30" r="2" fill="white" fillOpacity="0.05"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots)"/>
        </svg>
      </div>
      
      <FloatingParticles />

      <div className="relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="pt-8 pb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 glass-effect rounded-full animate-glow">
      
            <span className="text-white/90 text-sm font-medium">מיכלסון דותן ושות'</span>
          
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white/80 mb-4 font-bold animate-slide-up">
            צור קשר
          </h1>
          
          <p className="text-white/80 text-lg sm:text-xl max-w-md mx-auto leading-relaxed">
           ייעוץ משפטי מוביל עם דגש אישי ומקצועי
          </p>
          
     
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-lg mx-auto">
            <a
              href={`tel:${business.number}`}
              className="group flex flex-col items-center p-3 sm:p-4 glass-effect rounded-2xl hover:bg-white/20 transition-all duration-300 animate-float min-w-0 flex-1"
              style={{ animationDelay: '0.1s' }}
              title="התקשר אלינו"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform">
                <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-white/90 text-xs font-medium">התקשר</span>
            </a>

            <a
              href="https://wa.me/972532788686"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center p-3 sm:p-4 glass-effect rounded-2xl hover:bg-white/20 transition-all duration-300 animate-float min-w-0 flex-1"
              style={{ animationDelay: '0.2s' }}
              title="פתח צ'אט בוואטסאפ"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-green-600 rounded-xl flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform">
                <FaWhatsapp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-white/90 text-xs font-medium">וואטסאפ</span>
            </a>

            <a
              href={`mailto:${business.mail}`}
              className="group flex flex-col items-center p-3 sm:p-4 glass-effect rounded-2xl hover:bg-white/20 transition-all duration-300 animate-float min-w-0 flex-1"
              style={{ animationDelay: '0.3s' }}
              title="שלח מייל"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform">
                <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-white/90 text-xs font-medium">מייל</span>
            </a>

            <a
              href={`https://waze.com/ul?q=${encodeURIComponent(business.address)}&navigate=yes`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col items-center p-3 sm:p-4 glass-effect rounded-2xl hover:bg-white/20 transition-all duration-300 animate-float min-w-0 flex-1"
              style={{ animationDelay: '0.4s' }}
              title="נווט אלינו"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-400 to-purple-500 rounded-xl flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform">
                <FaWaze className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-white/90 text-xs font-medium">ניווט</span>
            </a>

            <button
              onClick={() => setShowHours(true)}
              className="group flex flex-col items-center p-3 sm:p-4 glass-effect rounded-2xl hover:bg-white/20 transition-all duration-300 animate-float min-w-0 flex-1"
              style={{ animationDelay: '0.5s' }}
              title="שעות פעילות"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mb-1 sm:mb-2 group-hover:scale-110 transition-transform">
                <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="text-white/90 text-xs font-medium">שעות</span>
            </button>
          </div>
        </div>

        <div className="max-w-md mx-auto mb-8">
        <div className="glass-effect rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/20">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 via-slate-600 to-slate-950 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Send className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">השאירו פרטים</h2>
            <p className="text-white/70 text-sm">נחזור אליכם בהקדם</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* שדה שם מלא */}
            <div className="relative">
              <label className="block text-sm font-medium text-white/90 mb-2">
                שם מלא
              </label>
              <input
                type="text"
                name="fullName"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full p-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-300 text-white placeholder-white/60 text-lg hover:bg-white/15"
                placeholder="הכנס שם מלא"
              />
            </div>

            {/* שדה טלפון */}
            <div className="relative">
              <label className="block text-sm font-medium text-white/90 mb-2">
                טלפון
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full p-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-300 text-white placeholder-white/60 text-lg hover:bg-white/15"
                placeholder="הכנס מספר טלפון"
                dir="rtl"
              />
            </div>

            {/* שדה נושא הפנייה */}
            <div className="relative">
              <label className="block text-sm font-medium text-white/90 mb-2">
                נושא הפנייה
              </label>
              <select
                name="subject"
                required
                value={formData.subject}
                onChange={handleInputChange}
                className="w-full p-4 bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-300 text-white appearance-none text-lg hover:bg-white/15"
              >
                <option value="" className="bg-slate-800 text-white">בחר נושא</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id} className="bg-slate-800 text-white">
                    {subject.title}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ שדה גובה החוב - מופיע רק אם בוחרים חדלות פירעון */}
            {isExecutionSubject() && (
              <div className="relative animate-slide-up">
                <label className="block text-sm font-medium text-white/90 mb-2">
                  מה גובה החוב? *
                </label>
                <div className="space-y-3">
                  {debtAmountOptions.map((option) => (
                    <label key={option.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="debtAmount"
                        value={option.value}
                        checked={formData.debtAmount === option.value}
                        onChange={handleInputChange}
                        required
                        className="sr-only"
                      />
                      <div className={`
                        w-full p-4 rounded-2xl border-2 transition-all duration-300 text-center font-medium
                        ${formData.debtAmount === option.value 
                          ? 'bg-blue-500/30 border-blue-400 text-white shadow-lg' 
                          : 'bg-white/10 border-white/20 text-white/80 hover:bg-white/15'
                        }
                      `}>
                        <div className="flex items-center justify-between">
                          <span>{option.label}</span>
                          <div className={`
                            w-5 h-5 rounded-full border-2 flex items-center justify-center
                            ${formData.debtAmount === option.value 
                              ? 'border-blue-400 bg-blue-400' 
                              : 'border-white/40'
                            }
                          `}>
                            {formData.debtAmount === option.value && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-white/60 text-xs mt-2">
                  * המידע יעזור לנו להתאים את הייעוץ הטוב ביותר עבורכם
                </p>
              </div>
            )}

            {/* כפתור שליחה */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full p-4 rounded-2xl text-lg font-bold
                flex items-center justify-center gap-3
                ${loading 
                  ? 'bg-gray-500/50 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-blue-500 via-slate-600 to-slate-950 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 animate-gradient'
                }
                text-white transition-all duration-300
                transform hover:scale-105 active:scale-95
                shadow-xl hover:shadow-2xl
                border border-white/20
              `}
            >
              {loading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  <span>שולח...</span>
                </>
              ) : (
                <>
                  <Send className="w-6 h-6" />
                  <span>שלח פנייה עכשיו</span>
                </>
              )}
            </button>
          </form>

            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="flex justify-center items-center gap-4 text-white/60 text-xs">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-400" />
                  <span>מאובטח</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-400" />
                  <span>ללא חיוב</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3 text-blue-400" />
                  <span>מענה מהיר</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={showHours} onClose={() => setShowHours(false)}>
        <div className="p-6 sm:p-8" dir="rtl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800">שעות פעילות</h3>
            </div>
            <button
              onClick={() => setShowHours(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center py-4 px-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200">
              <span className="font-bold text-gray-800">ראשון - חמישי</span>
              <span className="text-green-600 font-bold text-lg">09:00 - 18:00</span>
            </div>
            <div className="flex justify-between items-center py-4 px-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
              <span className="font-bold text-gray-800">שישי</span>
              <span className="text-red-500 font-bold">סגור</span>
            </div>
            <div className="flex justify-between items-center py-4 px-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
              <span className="font-bold text-gray-800">שבת</span>
              <span className="text-red-500 font-bold">סגור</span>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  <strong>שירות 24/7:</strong> במקרים דחופים ניתן להשאיר הודעה בטלפון
                </p>
                <p className="text-xs text-blue-600 mt-1">נחזור אליכם בהקדם האפשרי</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showSuccess} onClose={() => setShowSuccess(false)}>
        <SuccessAnimation />
      </Modal>
    </div>
  );
};

export default ContactPage;